import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "../lib/utils";

const Marquee: React.FC = () => {
  // A perpetually-scrolling band is the canonical trigger for motion sensitivity,
  // so when the user asks for reduced motion we freeze the scroll. WCAG 2.3.3.
  //
  // IMPORTANT: useReducedMotion returns false during SSR and the first client
  // render, then flips after mount. We therefore must NOT branch the rendered
  // markup on it (different child counts would cause a hydration mismatch,
  // React #418). Instead the DOM is identical in every case — four content
  // copies, overflow-hidden, mask-gradient — and only the framer-motion
  // animation is toggled. Framer mutates the transform via JS after hydration,
  // which never touches the server HTML, so this stays hydration-safe. (LL-0019)
  const reduceMotion = useReducedMotion();

  const content = [
    { text: "Coffee at 4", color: "bg-salmon" },
    { text: "Sunday morning run", color: "bg-ink/20" },
    { text: "Pizza after work", color: "bg-salmon" },
    { text: "Spontaneous picnic", color: "bg-ink/20" },
    { text: "Book club tonight", color: "bg-salmon" },
    { text: "Weekend walks", color: "bg-ink/20" },
    { text: "Just loop in", color: "bg-salmon" },
    { text: "Plans, not posts", color: "bg-ink/20" },
  ];

  // Four copies feed the seamless -50% scroll loop. Kept constant so the SSR and
  // client trees match; overflow-hidden on the band clips the overflow when the
  // scroll is frozen, leaving a static strip of phrases.
  const items = [...content, ...content, ...content, ...content];

  return (
    <div className="w-full overflow-hidden bg-ink py-5 border-y-2 border-ink">
      <div className="flex w-max mask-gradient">
        <motion.div
          className="flex gap-10 whitespace-nowrap px-6"
          animate={reduceMotion ? { x: 0 } : { x: "-50%" }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : {
                  repeat: Infinity,
                  ease: "linear",
                  duration: 40,
                }
          }
        >
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-10">
              <span className="font-serif italic text-2xl md:text-3xl text-canvas/90 tracking-tight">
                {item.text}
              </span>
              <div
                className={cn(
                  "w-2 h-2 rounded-full",
                  item.color === "bg-salmon" ? "bg-salmon" : "bg-canvas/40",
                )}
              />
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default Marquee;
